// Rug Room Shots: "Review" tab. Rows of silo | base room | result with
// Approve / Reject (reason) / Regenerate (note, same or other room, speed) / Clear,
// and the large side-by-side Compare view (CompareDialog, SPEC 9.11).
// Keyboard: A approve, R reject, G regenerate, C compare, J / K next / previous row,
// X tick / untick the row for Regenerate selected
// (only while no dialog is open and focus is not in a text field).
// Bulk regenerate (SPEC 10): tick rows (or "Select all N matching this filter"), then
// "Regenerate selected..." opens BulkRegenerateDialog (preview -> confirm with expected figures).
// Polls the current page every 5 s while the job has queued or running work, and every
// 60 s while only half-price work (batched / submitted) is left (SPEC 9.11).
// URL: ?tab=review&job=<id>&compare=<genId> reopens the compare view.
// 2026-10-21 build spec 4.2 / 9.3: a Reason filter (with counts) and "Include earlier attempts" (allAttempts):
// picking a reason or the Rejected chip turns it on. Earlier-attempt rows are view-only; their checkbox ticks the
// rug's CURRENT image (targetLatestGenId), and "Select all" then means "the current image of every rug in these rows".
// Review fix 2026-10-21: ticks made through an earlier-attempt row are remembered (checkEarlier), and Regenerate
// selected then starts with "Also regenerate N approved images" OFF (fromEarlierAttempts): such a tick often lands on
// an approved current image. The Reason menu counts every attempt (picking a reason shows earlier attempts).
// Rug binding is reject reason 8; Wrong style is disabled for sunroom shots (scene 'sun'); "Correct style" never
// offers Sunroom. Regenerate can send the rug's detail photo (DetailPhotoBlock) and choose the base room (the
// room picker marks rooms that are not the usual type for the rug). "Download images..." opens DownloadDialog.
// AI check (SPEC_AICHECK 5.4): an AI chip on every row (and "AI: why (problems)" under Fail / Needs a look), the
// "AI check" filter and the "Order" (AI: worst first) on their own row with "Check with AI...", "Regenerate by AI
// result...", "Approve AI-passed..." and the settings gear; the progress box adds the AI counts. An AI filter turns
// "Include earlier attempts" off and clears the Reason (AI results belong to current images); turning earlier
// attempts on, or picking a Reason, clears the AI filter. Reject pre-selects the AI's first problem; Regenerate
// offers "Use the AI's note". While the job has AI checks waiting or running the list re-reads every 10 s (only while
// the worker runs AI checks: features.aiCheck). "Stop queued checks (n)" sits on the AI row whenever the job has checks
// waiting, also with the check switched off on the worker (review fix RP-1).
// Review fix RP-2: the highlight (and A / R / G / C / X) follows the IMAGE, not the row number: when a re-read or the
// "AI: worst first" order moves rows, it stays on the same image, or moves to the image that followed it when that
// image left the list (approved in Needs review). Filter, order, page and job changes still start at the top.
// Every AI control hides with an older backend (no aiCounts in the response). The AI never acts by itself.
import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, Stack, Chip, Button, TextField, MenuItem, Alert, LinearProgress, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox, RadioGroup, Radio,
  TablePagination, Tooltip, Collapse, Switch, ListItemText, IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdThumbUp, MdThumbDown, MdReplay, MdUndo, MdInfoOutline, MdRefresh, MdKeyboard, MdExpandMore, MdExpandLess,
  MdCompare, MdDownload, MdFactCheck, MdDoneAll, MdSettings, MdAutoFixHigh, MdStopCircle,
} from 'react-icons/md';
import rrsApi from './api';
import RoomPicker from './RoomPicker';
import GenerationDetailDialog from './GenerationDetailDialog';
import CompareDialog from './CompareDialog';
import BulkRegenerateDialog from './BulkRegenerateDialog';
import DownloadDialog from './DownloadDialog';
import AiCheckDialog from './AiCheckDialog';
import BulkApproveDialog from './BulkApproveDialog';
import AiCheckSettingsDialog from './AiCheckSettingsDialog';
import AiRegenerateChooser from './AiRegenerateChooser';
import { requestAiCheck } from './AiCheckPanel';
import DetailPhotoBlock, {
  DETAIL_OFF, detailBody, detailReady, DETAIL_SOURCE_WORDS,
} from './DetailPhotoBlock';
import {
  AI_COLOR, primaryButtonSx, GEN_STATUS, REVIEW_STATE, JOB_STATUS, REJECT_REASON_LABELS, STYLE_FLAG_INFO,
  fmtUsd, fmtRate, fmtInt, styleName, shapeName, errText, manualStyles, conflictCode, serverText, WARN_CODES,
  usePolling, useNow, Tile, StyleChip, StatusChip, ImageLightbox, SILO_KIND, SunroomShotChip,
  isDailyLimit, dailyLimitText, overBudget, overBudgetText, signalSpend,
  isEarlierAttempt, reasonBlocked, WRONG_STYLE_TWIN_TIP, ConfirmDialog,
  AiChip, AI_FILTERS, AI_NOT_READY_TEXT, aiCountsText, aiHasResult, aiProblemsText, aiSuggestedReason,
} from './common';
import {
  letterLabel, roomLetterOf, roomPickWarnings, describeLetters,
} from './roomRules';
import {
  SpeedChip, SpeedChooser, BatchesSection, speedsFromConfig, rateFor, defaultSpeed, resultStatusContent,
  regenWord, isBatch, batchUnavailableFromError,
} from './speed';

// Review fix RP-2: where the highlight goes when the list changes under it. The same image when it is still listed;
// else the first image AFTER it (in the order shown before) that is still listed; else the one before it; else the
// same row number (clamped).
export const followSelection = (genId, prevOrder, items, fallbackIdx) => {
  const at = items.findIndex((it) => it.genId === genId);
  if (at >= 0) return at;
  const pos = new Map(items.map((it, i) => [it.genId, i]));
  const p = (prevOrder || []).indexOf(genId);
  if (p >= 0) {
    for (let i = p + 1; i < prevOrder.length; i += 1) if (pos.has(prevOrder[i])) return pos.get(prevOrder[i]);
    for (let i = p - 1; i >= 0; i -= 1) if (pos.has(prevOrder[i])) return pos.get(prevOrder[i]);
  }
  return Math.max(0, Math.min(items.length - 1, fallbackIdx));
};

const PAGE_SIZE = 24;
const STATE_FILTERS = [
  { key: 'pending', label: 'Needs review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'failed', label: 'Failed' },
  { key: 'unknown', label: 'Unknown' },
  { key: 'in_progress', label: 'In progress' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];
const REGEN_FROM = ['succeeded', 'failed', 'unknown', 'cancelled'];

const isTyping = (el) => {
  if (!el) return false;
  const tag = (el.tagName || '').toLowerCase();
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
};

// The genId a row's checkbox ticks: its own (same rule as the backend's skip check: finished, the target's latest
// attempt, not superseded), or for an earlier attempt the rug's current image (build spec 4.2). null = none.
const tickId = (it) => {
  if (!it) return null;
  if (isEarlierAttempt(it)) return it.targetLatestGenId ? Number(it.targetLatestGenId) : null;
  return REGEN_FROM.includes(it.status) && !!it.isLatest && !it.supersededByGenId ? it.genId : null;
};
const checkable = (it) => tickId(it) !== null;

// ---------------------------------------------------------------------------
// One panel of a row (silo, base room or result).
// ---------------------------------------------------------------------------
const Panel = ({ label, sub, src, onClick, children }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }} color="text.secondary">{label}</Typography>
      {sub && <Typography variant="caption" color="text.secondary" noWrap sx={{ fontFamily: 'monospace', ml: 1 }}>{sub}</Typography>}
    </Stack>
    {children || (
      <Tile src={src} alt={label} size={240} onClick={onClick} sx={{ width: '100%', height: 'auto', aspectRatio: '1 / 1' }} />
    )}
  </Box>
);

const ResultPanel = ({ item, now, onOpen }) => {
  const theme = useTheme();
  const box = (content, color) => (
    <Box
      sx={{
        width: '100%', aspectRatio: '1 / 1', borderRadius: 1, border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.mode === 'dark' ? '#2a2a2a' : '#f5f3f0',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 2, textAlign: 'center',
        color,
      }}
    >
      {content}
    </Box>
  );
  if (item.status === 'succeeded' && item.result) {
    return <Tile src={item.result.thumbUrl || item.result.previewUrl} alt="result" size={240} onClick={onOpen} sx={{ width: '100%', height: 'auto', aspectRatio: '1 / 1' }} />;
  }
  // Spinner (Fast), clock (Half price) or the error in words (shared with the compare view).
  const st = resultStatusContent(item, now, false);
  return box(st.content, st.color);
};

// ---------------------------------------------------------------------------
// One review row.
// ---------------------------------------------------------------------------
const ReviewRow = React.forwardRef(({
  item, selected, onSelect, now, styles, shapes, usdPerImage, busy,
  onApprove, onReject, onRegenerate, onClear, onOpenLightbox, onDetails, onCompare,
  checked, checkable, checkLocked, checkTotal, onCheck, letterNames,
}, ref) => {
  const [showEarlier, setShowEarlier] = useState(false);
  const silo = item.silo || {};
  const base = item.base || {};
  // An earlier attempt (Include earlier attempts): view-only; its checkbox ticks the rug's current image.
  const earlierRow = isEarlierAttempt(item);
  const succeeded = item.status === 'succeeded';
  const canDecide = succeeded && !earlierRow;
  const canRegen = REGEN_FROM.includes(item.status) && !item.supersededByGenId && !earlierRow;
  // The backend computes badges; fall back to local checks if a field is missing.
  const badges = item.badges || {};
  const sizeMismatch = badges.sizeMismatch !== undefined ? badges.sizeMismatch : (item.errorCode === 'size_mismatch'
    || !!(item.result && item.result.outW && item.result.outH && item.result.outW !== item.result.outH));
  const roomStyle = item.roomCode ? item.roomCode.slice(0, 2) : null;
  const roomMismatch = badges.roomStyleMismatch !== undefined ? badges.roomStyleMismatch : !!(roomStyle && item.styleCode && roomStyle !== item.styleCode);
  const borrowed = silo.kind === 'sibling' || (badges.borrowedSilo && silo.kind !== 'design_fallback');
  const designPhoto = silo.kind === 'design_fallback';
  const earlier = item.previousAttempts || [];
  const roomLetterCode = roomLetterOf(item.roomCode);
  const detail = item.detail || null;
  // AI: a caption with the reason only for Fail / Needs a look (nothing extra for a Pass).
  const aiFlag = !!item.aiCheck && (item.aiCheck.state === 'fail' || item.aiCheck.state === 'look');
  const aiProblems = aiFlag ? aiProblemsText(item.aiCheck) : '';

  return (
    <Paper
      ref={ref}
      variant="outlined"
      onClick={onSelect}
      sx={{
        p: 1.5, borderRadius: 2, borderWidth: 2,
        borderColor: selected ? AI_COLOR : 'divider',
        boxShadow: selected ? `0 0 0 2px ${AI_COLOR}22` : 'none',
      }}
    >
      <Stack direction="row" spacing={1.5}>
        <Panel label="Silo" sub={item.itemId} src={silo.smallUrl} onClick={() => onOpenLightbox('silo')} />
        <Panel label="Base room" sub={item.roomCode} src={base.smallUrl} onClick={() => onOpenLightbox('base')} />
        <Panel label="Result" sub={item.result && item.result.outW ? `${item.result.outW}x${item.result.outH}` : null}>
          <ResultPanel item={item} now={now} onOpen={() => onOpenLightbox('result')} />
        </Panel>
      </Stack>

      {/* Meta line */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 0.75 }}>
        <Typography variant="body2" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{item.itemId}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{item.webId}</Typography>
        <Tooltip title={shapeName(item.shapeCode, shapes)}><Chip size="small" variant="outlined" label={item.shapeCode} /></Tooltip>
        <StyleChip code={item.styleCode} styles={styles} />
        {item.scene === 'sun' && <SunroomShotChip />}
        <Tooltip title={roomLetterCode ? `Room type ${letterLabel(roomLetterCode, letterNames)}` : ''}>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{item.roomCode}</Typography>
        </Tooltip>
        <Chip size="small" variant="outlined" label={`attempt ${item.attempt}`} />
        {isBatch(item.speed) && <SpeedChip speed={item.speed} />}
        {item.costUsd !== null && item.costUsd !== undefined && <Typography variant="body2" color="text.secondary">{fmtUsd(item.costUsd, 3)}</Typography>}
        {item.latencyMs ? <Typography variant="body2" color="text.secondary">{(item.latencyMs / 1000).toFixed(0)}s</Typography> : null}
        <StatusChip map={GEN_STATUS} status={item.status} />
        {succeeded && <StatusChip map={REVIEW_STATE} status={item.reviewState} />}
        {item.aiCheck ? <AiChip ai={item.aiCheck} /> : null}
        {item.reviewReasonCode && <Chip size="small" color="error" variant="outlined" label={REJECT_REASON_LABELS[item.reviewReasonCode] || item.reviewReasonCode} />}
        {sizeMismatch && <Tooltip title="The output is not 4K square."><Chip size="small" color="warning" label="size mismatch" /></Tooltip>}
        {borrowed && <Tooltip title={SILO_KIND.sibling.title}><Chip size="small" color="warning" variant="outlined" label="borrowed silo" /></Tooltip>}
        {designPhoto && <Tooltip title={SILO_KIND.design_fallback.title}><Chip size="small" color="secondary" variant="outlined" label="design photo" /></Tooltip>}
        {roomMismatch && <Tooltip title={`Room style ${styleName(roomStyle, styles)} differs from the rug's style.`}><Chip size="small" color="warning" variant="outlined" label="room_style_mismatch" /></Tooltip>}
        {badges.roomRuleMismatch && (
          <Tooltip title={`${STYLE_FLAG_INFO.room_rule_mismatch.text}${item.roomLetters ? ` It usually uses ${describeLetters(item.roomLetters, letterNames)} rooms.` : ''}`}>
            <Chip size="small" color="warning" variant="outlined" label="not the usual room type" />
          </Tooltip>
        )}
        {badges.baseShapeMismatch && (
          <Tooltip title={STYLE_FLAG_INFO.room_base_shape_mismatch.text}>
            <Chip size="small" color="warning" variant="outlined" label="oval base photo" />
          </Tooltip>
        )}
        {detail && (
          <Tooltip title={`Detail photo sent: ${detail.imageKey || ''}${detail.source ? ` (${DETAIL_SOURCE_WORDS[detail.source] || detail.source})` : ''}`}>
            <Chip
              size="small" color="info" variant="outlined" label="Detail photo sent"
              onClick={detail.smallUrl || detail.largeUrl ? (e) => { e.stopPropagation(); onOpenLightbox('detail'); } : undefined}
            />
          </Tooltip>
        )}
      </Stack>
      {aiFlag && (item.aiCheck.why || aiProblems) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          <Box component="span" sx={{ fontWeight: 700, color: item.aiCheck.state === 'fail' ? 'error.main' : 'warning.main' }}>AI:</Box>
          {' '}{item.aiCheck.why || ''}{aiProblems ? `${item.aiCheck.why ? ' ' : ''}(${aiProblems})` : ''}
        </Typography>
      )}
      {(item.note || item.reviewNote || item.reviewedByEmail) && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
          {item.note ? `Regenerate note: "${item.note}". ` : ''}
          {item.reviewNote ? `Review note: "${item.reviewNote}". ` : ''}
          {item.reviewedByEmail ? `Reviewed by ${item.reviewedByEmail}.` : ''}
        </Typography>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, flexWrap: 'wrap', rowGap: 1 }}>
        <Tooltip
          title={checkLocked
            ? `All ${fmtInt(checkTotal)} matching rows are selected. Clear the selection to pick rows one by one.`
            : (earlierRow
              ? (checkable ? `Ticks this rug's current image (gen ${item.targetLatestGenId})` : 'The current image of this rug is not known.')
              : (!checkable ? "Can't regenerate this row (still in progress, or a newer attempt exists)." : 'Tick for Regenerate selected (X)'))}
        >
          <span>
            <Checkbox
              size="small" sx={{ p: 0.5 }}
              checked={checkLocked ? checkable : !!checked}
              disabled={!checkable || checkLocked}
              onClick={(e) => e.stopPropagation()}
              onChange={onCheck}
              inputProps={{ 'aria-label': 'Select for Regenerate selected' }}
            />
          </span>
        </Tooltip>
        <Button
          size="small" variant={item.reviewState === 'approved' ? 'contained' : 'outlined'} color="success"
          startIcon={<MdThumbUp />} disabled={!canDecide || busy} onClick={(e) => { e.stopPropagation(); onApprove(); }}
        >
          Approve
        </Button>
        <Button
          size="small" variant={item.reviewState === 'rejected' ? 'contained' : 'outlined'} color="error"
          startIcon={<MdThumbDown />} disabled={!canDecide || busy} onClick={(e) => { e.stopPropagation(); onReject(); }}
        >
          Reject
        </Button>
        <Button
          size="small" variant="outlined" startIcon={<MdReplay />} disabled={!canRegen || busy}
          onClick={(e) => { e.stopPropagation(); onRegenerate(); }}
          sx={{ color: AI_COLOR, borderColor: `${AI_COLOR}88` }}
        >
          {regenWord(item.status)} · {fmtRate(usdPerImage)}
        </Button>
        <Tooltip title="Large side-by-side view with synced zoom (C)">
          <Button size="small" startIcon={<MdCompare />} onClick={(e) => { e.stopPropagation(); onCompare(); }}>Compare</Button>
        </Tooltip>
        {canDecide && item.reviewState !== 'pending' && (
          <Button size="small" startIcon={<MdUndo />} disabled={busy} onClick={(e) => { e.stopPropagation(); onClear(); }}>Clear decision</Button>
        )}
        <Button size="small" startIcon={<MdInfoOutline />} onClick={(e) => { e.stopPropagation(); onDetails(item.genId); }}>Details</Button>
        {earlier.length > 0 && (
          <Button
            size="small" endIcon={showEarlier ? <MdExpandLess /> : <MdExpandMore />}
            onClick={(e) => { e.stopPropagation(); setShowEarlier((s) => !s); }}
          >
            {showEarlier ? 'Hide' : 'Show'} earlier attempts ({earlier.length})
          </Button>
        )}
        {busy && <CircularProgress size={18} />}
        {earlierRow ? (
          <Typography variant="caption" color="text.secondary">
            attempt {item.attempt} of this rug; the current image is gen {item.targetLatestGenId || item.supersededByGenId || '?'}
          </Typography>
        ) : null}
        {!earlierRow && item.supersededByGenId ? <Typography variant="caption" color="text.secondary">regenerated as gen {item.supersededByGenId}</Typography> : null}
      </Stack>
      <Collapse in={showEarlier} unmountOnExit>
        <Stack spacing={0.5} sx={{ mt: 1, pl: 1, borderLeft: `3px solid ${AI_COLOR}33` }}>
          {earlier.map((p) => (
            <Stack key={p.genId} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
              <Typography variant="body2">Attempt {p.attempt}</Typography>
              <StatusChip map={GEN_STATUS} status={p.status} />
              {p.status === 'succeeded' && <StatusChip map={REVIEW_STATE} status={p.reviewState} />}
              {p.reviewReasonCode && <Typography variant="caption" color="text.secondary">{REJECT_REASON_LABELS[p.reviewReasonCode] || p.reviewReasonCode}</Typography>}
              {p.roomCode && <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.roomCode}</Typography>}
              <Button size="small" onClick={(e) => { e.stopPropagation(); onDetails(p.genId); }}>View</Button>
            </Stack>
          ))}
        </Stack>
      </Collapse>
    </Paper>
  );
});
ReviewRow.displayName = 'ReviewRow';

// ---------------------------------------------------------------------------
const ReviewTab = ({ config, jobId, onJobChange, notify, health }) => {
  const theme = useTheme();
  const styles = config.styles;
  const speeds = useMemo(() => speedsFromConfig(config, health), [config, health]);
  const [params, setParams] = useSearchParams();
  const compareGenId = parseInt(params.get('compare') || '', 10) || null;
  const [jobs, setJobs] = useState([]);
  const [jobDetail, setJobDetail] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stateFilter, setStateFilter] = useState('pending');
  const [styleFilter, setStyleFilter] = useState('');
  const [shapeFilter, setShapeFilter] = useState('');
  // Reason filter + "Include earlier attempts" (build spec 4.2). A reason implies Rejected.
  const [reasonFilter, setReasonFilter] = useState('');
  const [allAttempts, setAllAttempts] = useState(false);
  // AI check filter ('' = all) and order ('default' = plan order, 'ai_worst'), SPEC_AICHECK 4.5 / 5.4.
  const [aiFilter, setAiFilter] = useState('');
  const [sortOrder, setSortOrder] = useState('default');
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(0);
  // Rows with a review request in flight (a set: the compare view can decide row 9 while
  // row 8's request is still pending, and each row stays busy until its OWN request ends).
  const [busyGens, setBusyGens] = useState(() => new Set());
  const [lightbox, setLightbox] = useState(null);
  const [detailGen, setDetailGen] = useState(null);
  const [showKeys, setShowKeys] = useState(false);

  const [rejectItem, setRejectItem] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [rejectStyle, setRejectStyle] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);
  const [rejectSuggested, setRejectSuggested] = useState(null); // the reason the AI check suggested, if any

  const [regenItem, setRegenItem] = useState(null);
  const [regenNote, setRegenNote] = useState('');
  const [regenRoomMode, setRegenRoomMode] = useState('same');
  const [regenRoom, setRegenRoom] = useState('');
  const [regenInPrompt, setRegenInPrompt] = useState(false);
  const [regenSpeed, setRegenSpeed] = useState('fast');
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenRoomObj, setRegenRoomObj] = useState(null); // the chosen room (letter / base shape warnings)
  const [regenRooms, setRegenRooms] = useState([]); // the picker's rooms (to find a preselected room)
  const [regenDetail, setRegenDetail] = useState(DETAIL_OFF);
  const [roomPreview, setRoomPreview] = useState(null); // a room from the picker, shown large

  // Bulk regenerate (SPEC 10). `checked` holds genIds across pages; `checkAll` means "all rows
  // matching the current filter" (the backend resolves the filter, so pages never need loading).
  // The dialog gets the selection frozen at the moment it opens.
  const [checked, setChecked] = useState(() => new Set()); // genIds
  const [checkAll, setCheckAll] = useState(false); // "all N matching this filter"
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkSelection, setBulkSelection] = useState(null);
  const [bulkDetailDefault, setBulkDetailDefault] = useState(false);
  // genId ticked -> the reject reason of the row that ticked it (bulk "detail photo" default: all Rug binding).
  const checkReasons = useRef(new Map());
  // genIds ticked through an earlier-attempt row (they stand for the rug's current image, not the row on screen).
  const checkEarlier = useRef(new Set());
  const [bulkFromEarlier, setBulkFromEarlier] = useState(false);
  // "Regenerate by AI result": the bulk dialog opens with the AI's notes ticked and a default common note.
  const [bulkAiDefaults, setBulkAiDefaults] = useState(null);

  // AI check dialogs (each mounted only while open; selections frozen at the moment they open).
  const [aiCheckSel, setAiCheckSel] = useState(null); // { selection, selectAll, total, filter }
  const [approveSel, setApproveSel] = useState(null); // { filter: { style, shape }, ticked }
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false);
  const [aiRegenOpen, setAiRegenOpen] = useState(false);
  // "Stop queued checks" on the AI row (review fix RP-1): reachable also when the worker's check is off.
  const [stopAsk, setStopAsk] = useState(false);
  const [stopBusy, setStopBusy] = useState(false);

  const seq = useRef(0);
  const rowRefs = useRef({});
  const smallMax = config.roomRules && config.roomRules.effective ? config.roomRules.effective.round_small_max : undefined;

  // ---- data ------------------------------------------------------------------
  const loadJobs = useCallback(async () => {
    try {
      const res = await rrsApi.listJobs({ page: 1, pageSize: 100 });
      setJobs(res.items || []);
      return res.items || [];
    } catch (e) {
      return [];
    }
  }, []);

  const load = useCallback(async () => {
    if (!jobId) return;
    const my = seq.current + 1;
    seq.current = my;
    setLoading(true);
    try {
      const res = await rrsApi.getReview(jobId, {
        state: stateFilter,
        style: styleFilter,
        shape: shapeFilter,
        reason: reasonFilter,
        allAttempts: allAttempts ? 'true' : 'false',
        ai: aiFilter || undefined,
        sort: sortOrder !== 'default' ? sortOrder : undefined,
        page: page + 1,
        pageSize: PAGE_SIZE,
      });
      if (my === seq.current) { setData(res); setError(''); }
    } catch (e) {
      if (my === seq.current) setError(errText(e));
    } finally {
      if (my === seq.current) setLoading(false);
    }
  }, [jobId, stateFilter, styleFilter, shapeFilter, reasonFilter, allAttempts, aiFilter, sortOrder, page]);

  // Job detail: its speed (the Regenerate default) and its half-price batches.
  const loadJobDetail = useCallback(async () => {
    if (!jobId) return;
    try {
      const j = await rrsApi.getJob(jobId);
      setJobDetail(j && j.jobId === jobId ? j : null);
    } catch (e) {
      /* the review list shows its own errors */
    }
  }, [jobId]);

  useEffect(() => { loadJobs(); }, [loadJobs]);
  useEffect(() => { setJobDetail(null); loadJobDetail(); }, [loadJobDetail]);
  // No job in the URL: open the most recent started job.
  useEffect(() => {
    if (jobId || !jobs.length) return;
    const first = jobs.find((j) => j.startedAt) || jobs[0];
    onJobChange(first.jobId, { replace: true });
  }, [jobId, jobs, onJobChange]);

  // A new job starts at the top (the empty list in between clears the highlight's anchor, RP-2).
  useEffect(() => { setData(null); setPage(0); setSelected(0); }, [jobId]);
  useEffect(() => {
    setChecked(new Set());
    setCheckAll(false);
    checkReasons.current = new Map();
    checkEarlier.current = new Set();
  }, [jobId, stateFilter, styleFilter, shapeFilter, reasonFilter, allAttempts, aiFilter]);
  useEffect(() => { load(); }, [load]);

  const progress = (data && data.progress) || { done: 0, total: 0, queued: 0, running: 0 };
  const items = useMemo(() => (data && data.items) || [], [data]);

  const toggleCheck = useCallback((genId, reason, earlier) => {
    if (checkAll || !genId) return;
    setChecked((cur) => {
      const next = new Set(cur);
      if (next.has(genId)) {
        next.delete(genId);
        checkReasons.current.delete(genId);
        checkEarlier.current.delete(genId);
      } else {
        next.add(genId);
        checkReasons.current.set(genId, reason || null);
        if (earlier) checkEarlier.current.add(genId);
      }
      return next;
    });
  }, [checkAll]);
  // Half-price work at Google: progress.atGoogle (or batched + submitted from an older shape).
  const atGoogle = progress.atGoogle !== undefined && progress.atGoogle !== null
    ? Number(progress.atGoogle) || 0
    : (Number(progress.batched) || 0) + (Number(progress.submitted) || 0);
  const fastActive = (progress.queued || 0) + (progress.running || 0) > 0;
  const batchActive = atGoogle > 0;
  const active = fastActive || batchActive;
  // AI check (SPEC_AICHECK 4.5): aiCounts / aiOpen / aiThresholds come only from a backend that has the AI check.
  const aiCounts = (data && data.aiCounts) || null;
  const aiAvailable = !!aiCounts;
  const aiOn = !!(config.features && config.features.aiCheck);
  const aiOpenNow = (data && data.aiOpen) || {};
  const aiQueued = Number(aiOpenNow.queued) || 0;
  const aiWaiting = aiQueued + (Number(aiOpenNow.running) || 0);
  const aiChecking = aiWaiting > 0;
  const aiThresholds = (data && data.aiThresholds) || config.aiCheck || null;
  // The merlot tool colour is too dark on a dark background (as in the compare view): a light tint there.
  const aiAccent = theme.palette.mode === 'dark' ? '#E3A6AE' : AI_COLOR;
  // AI checks only move while the worker runs them (features.aiCheck): with the check switched off on the worker, or a
  // worker without it, queued checks wait and re-reading every 10 s would change nothing (review fix RP-1).
  const aiMoving = aiChecking && aiOn;
  usePolling(load, 5000, !!jobId && fastActive);
  // While AI checks wait or run (the 5 s poll already covers a busy job), every 10 s.
  usePolling(load, 10000, !!jobId && !fastActive && aiMoving);
  usePolling(load, 60000, !!jobId && !fastActive && !aiMoving && batchActive);
  usePolling(loadJobDetail, 60000, !!jobId && batchActive);
  const fastRows = items.some((i) => (i.status === 'queued' || i.status === 'running') && !isBatch(i.speed));
  const batchRows = items.some((i) => ['batched', 'submitted'].includes(i.status) || (i.status === 'queued' && isBatch(i.speed)));
  const now = useNow(fastRows || batchRows, fastRows ? 1000 : 30000);
  const jobSpeed = (jobDetail && jobDetail.speed) || null;

  // Review fix RP-2: the highlight follows the image. `anchor` is the list as last shown, the highlighted row and image.
  // When a new list arrives (a re-read, the AI order moving rows, a merged review result) the highlight stays on the
  // image it was on, or on the image a move in the same update went to (counted in the list the person saw), or on the
  // image that followed it when it left the list. An explicit reset (filter, order, page) waits for its own list and
  // starts at the top. Runs before paint, so a key press never acts on a row that is about to move. (It also keeps the
  // highlight inside a shorter list; a separate clamp effect could undo the move with a stale index.)
  const anchor = useRef({ items: null, sel: 0, genId: null, order: [], reset: false });
  const resetSelection = () => {
    anchor.current = { items, sel: 0, genId: null, order: [], reset: true };
    setSelected(0);
  };
  useLayoutEffect(() => {
    const a = anchor.current;
    let want = selected;
    if (a.reset) {
      if (a.items === items && selected === 0) return; // still the old list: wait for the reset's own list
    } else if (a.items && a.items !== items && items.length) {
      const meant = selected !== a.sel ? a.order[selected] : a.genId;
      if (meant !== undefined && meant !== null) want = followSelection(meant, a.order, items, selected);
    }
    if (items.length && want >= items.length) want = items.length - 1;
    if (want !== selected) {
      anchor.current = { ...a, items, reset: false }; // this list is handled; the next run records `want`
      setSelected(want);
      return;
    }
    anchor.current = {
      items, sel: selected, genId: items[selected] ? items[selected].genId : null, order: items.map((it) => it.genId), reset: false,
    };
  }, [items, selected]);

  const mergeItem = (genId, patch) => setData((cur) => (cur ? {
    ...cur, items: cur.items.map((it) => (it.genId === genId ? { ...it, ...patch } : it)),
  } : cur));

  // ---- actions --------------------------------------------------------------
  const decide = useCallback(async (item, body) => {
    const gid = item.genId;
    setBusyGens((cur) => new Set(cur).add(gid));
    try {
      const res = await rrsApi.reviewGeneration(item.genId, body);
      const fallbackState = body.decision === 'approve' ? 'approved' : body.decision === 'reject' ? 'rejected' : 'pending';
      const patch = {
        reviewState: fallbackState,
        reviewReasonCode: body.decision === 'reject' ? body.reasonCode : null,
        reviewNote: body.decision === 'clear' ? null : (body.note || null),
        ...(res && typeof res === 'object' ? res : {}),
      };
      mergeItem(item.genId, patch);
      return patch; // truthy: the compare view merges it into rows it opened from a link
    } catch (e) {
      // 409 scene_list_only (Sunroom by hand) / twin_style_fixed: the server's words as a warning.
      const code = conflictCode(e);
      notify(code && WARN_CODES.includes(code) ? serverText(e) : errText(e), code ? 'warning' : 'error');
      return null;
    } finally {
      setBusyGens((cur) => {
        if (!cur.has(gid)) return cur;
        const next = new Set(cur);
        next.delete(gid);
        return next;
      });
    }
  }, [notify]);

  // Keyboard / auto-advance moves scroll the newly selected row into view;
  // clicks and background polls never scroll the page.
  const scrollPending = useRef(false);
  const moveSelection = useCallback((delta) => {
    scrollPending.current = true;
    setSelected((cur) => Math.max(0, Math.min(items.length - 1, cur + delta)));
  }, [items.length]);

  useEffect(() => {
    if (!scrollPending.current) return;
    scrollPending.current = false;
    const it = items[selected];
    const el = it ? rowRefs.current[it.genId] : null;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selected, items]);

  const approve = useCallback(async (item) => {
    if (!item || item.status !== 'succeeded' || isEarlierAttempt(item) || busyGens.has(item.genId)) return;
    const ok = await decide(item, { decision: 'approve' });
    if (ok) moveSelection(1);
  }, [decide, moveSelection, busyGens]);

  const openReject = useCallback((item) => {
    if (!item || item.status !== 'succeeded' || isEarlierAttempt(item)) return;
    // The AI's first problem is pre-selected when the AI failed or flagged the image and it has no reason yet.
    const suggested = aiSuggestedReason(item, config.rejectReasons);
    setRejectItem(item);
    setRejectReason(item.reviewReasonCode || suggested || '');
    setRejectSuggested(suggested);
    setRejectNote(item.reviewNote || '');
    setRejectStyle('');
  }, [config.rejectReasons]);

  const submitReject = async () => {
    if (!rejectItem || !rejectReason) return;
    setRejectBusy(true);
    const ok = await decide(rejectItem, {
      decision: 'reject', reasonCode: rejectReason, note: rejectNote.trim(),
      correctStyle: rejectReason === 'wrong_style' ? rejectStyle : undefined,
    });
    setRejectBusy(false);
    if (ok) {
      if (rejectReason === 'wrong_style') notify(`Style corrected to ${rejectStyle}. Regenerate to place it in a ${rejectStyle} room.`, 'info');
      setRejectItem(null);
      moveSelection(1);
    }
  };

  const openRegen = useCallback((item) => {
    if (!item || !REGEN_FROM.includes(item.status) || isEarlierAttempt(item)) return;
    const styleChanged = item.targetStyleCode && item.targetStyleCode !== item.styleCode;
    setRegenItem(item);
    setRegenNote(item.status === 'failed' || item.status === 'unknown' ? 'Retry after failure' : '');
    setRegenRoomMode(styleChanged ? 'other' : 'same');
    setRegenRoom('');
    setRegenRoomObj(null);
    setRegenRooms([]);
    setRegenDetail(DETAIL_OFF);
    setRegenInPrompt(false);
    setRegenSpeed(defaultSpeed(jobSpeed || item.speed, speeds));
    if (styleChanged) {
      // Backend rule: least-used active room of the NEW style x shape, of the right type for this rug (targetId).
      rrsApi.suggestRoom(item.targetStyleCode, item.shapeCode, item.roomCode, item.targetId)
        .then((res) => { if (res && res.roomCode) setRegenRoom(res.roomCode); })
        .catch(() => {});
    }
  }, [jobSpeed, speeds]);

  // When the target's style was overridden since this attempt, preselect the
  // least-used active room of the new style (RoomPicker sorts that one first).
  // The picker sorts same-style rooms of the right type first, so the first one is the best preselection.
  const onRegenRoomsLoaded = useCallback((rooms) => {
    setRegenRoom((cur) => {
      if (cur) return cur;
      if (!regenItem) return cur;
      const wanted = regenItem.targetStyleCode || regenItem.styleCode;
      const first = rooms.find((r) => r.styleCode === wanted && r.roomCode !== regenItem.roomCode)
        || rooms.find((r) => r.roomCode !== regenItem.roomCode);
      return first ? first.roomCode : cur;
    });
  }, [regenItem]);
  const onRegenRoomsLoadedAll = useCallback((rooms) => {
    setRegenRooms(rooms || []);
    onRegenRoomsLoaded(rooms || []);
  }, [onRegenRoomsLoaded]);

  // One new attempt (dialog or compare view). Returns true when it was queued.
  const doRegenerate = useCallback(async (item, body) => {
    try {
      const res = await rrsApi.regenerate(item.genId, body);
      signalSpend();
      const half = isBatch(body.speed);
      notify(
        `Queued attempt ${res.attempt} for ${item.itemId}${half ? ' at half price' : ''} (about ${fmtUsd(res.estUsd, 3)}).${half ? ' Google usually returns it within hours, at most 24 h.' : ''}`,
        'success',
      );
      await load();
      if (half) loadJobDetail();
      return true;
    } catch (e) {
      const code = e.status === 409 && e.body ? e.body.code : null;
      if (code === 'already_in_progress') {
        notify('Another attempt for this rug is already queued, generating or at Google.', 'warning');
      } else if (code === 'not_latest') {
        notify('A newer attempt of this rug exists. Refresh.', 'warning');
        load();
      } else if (code && WARN_CODES.includes(code)) {
        // room_not_active, no_base, no_detail_photo, detail_not_found, worker_not_ready, twin_style_fixed,
        // scene_list_only, scene_room_style: the server's words.
        notify(serverText(e), 'warning');
      } else if (code === 'batch_unavailable') {
        notify(`${batchUnavailableFromError(e)} Choose Fast instead.`, 'warning');
      } else if (isDailyLimit(e)) {
        // Regenerate / Retry over the non-admin daily limit: the server's words, no automatic retry.
        notify(dailyLimitText(e), 'error');
        signalSpend();
      } else {
        notify(errText(e), 'error');
      }
      return false;
    }
  }, [notify, load, loadJobDetail]);

  // AI check of one image (the compare view's Check again): queue it, show the row as being checked, re-read the
  // list (its 10 s poll then follows the check). Returns the row patch, or null when nothing was queued.
  const requestCheck = useCallback(async (item) => {
    if (!item) return null;
    const r = await requestAiCheck(item.genId, item.itemId);
    notify(r.message, r.severity);
    if (!r.ok && !r.already) return null;
    const prev = item.aiCheck || {};
    const patch = { aiCheck: { ...prev, checking: true, state: aiHasResult(prev) ? prev.state : 'checking' } };
    setData((cur) => (cur ? {
      ...cur, items: cur.items.map((it) => (it.genId === item.genId ? { ...it, ...patch } : it)),
    } : cur));
    load();
    return patch;
  }, [notify, load]);

  const submitRegen = async () => {
    if (!regenItem || regenNote.trim().length < 3) return;
    if (regenRoomMode === 'other' && !regenRoom) return;
    if (!detailReady(regenDetail)) return;
    setRegenBusy(true);
    const ok = await doRegenerate(regenItem, {
      note: regenNote.trim(),
      roomCode: regenRoomMode === 'other' ? regenRoom : null,
      noteInPrompt: regenInPrompt,
      speed: regenSpeed,
      ...detailBody(regenDetail),
    });
    setRegenBusy(false);
    if (ok) setRegenItem(null);
  };
  // The chosen room (from the picker's list, also when preselected) and its warnings (spec 4.7).
  const regenChosenRoom = regenRoomMode === 'other' && regenRoom
    ? (regenRoomObj && regenRoomObj.roomCode === regenRoom ? regenRoomObj : regenRooms.find((r) => r.roomCode === regenRoom) || null)
    : null;
  const regenWarnings = regenItem ? roomPickWarnings(regenItem, regenChosenRoom, smallMax, config.roomLetters) : [];

  // ---- compare view (URL ?compare=<genId>) --------------------------------------------
  const setCompare = useCallback((genId) => {
    const sp = new URLSearchParams(params);
    if (genId) sp.set('compare', String(genId));
    else sp.delete('compare');
    setParams(sp, { replace: true });
  }, [params, setParams]);

  const openCompare = useCallback((item) => { if (item) setCompare(item.genId); }, [setCompare]);
  const closeCompare = useCallback(() => {
    // Back on the list, the row last shown stays selected and scrolls into view.
    const idx = items.findIndex((it) => it.genId === compareGenId);
    if (idx >= 0) { scrollPending.current = true; setSelected(idx); }
    setCompare(null);
  }, [items, compareGenId, setCompare]);
  const onCompareNavigate = useCallback((genId) => {
    const idx = items.findIndex((it) => it.genId === genId);
    if (idx >= 0) setSelected(idx);
    setCompare(genId);
  }, [items, setCompare]);
  const onComparePage = useCallback((p) => setPage(p), []);

  const openLightbox = useCallback((item, key) => {
    const silo = item.silo || {};
    const base = item.base || {};
    const res = item.result || {};
    setLightbox({
      title: `${item.itemId} · ${item.roomCode} · attempt ${item.attempt}`,
      initialKey: key,
      items: [
        { key: 'silo', label: 'Silo', src: silo.originalUrl || silo.smallUrl, downloadUrl: silo.originalUrl, downloadLabel: 'Open original' },
        { key: 'base', label: 'Base room', src: base.originalUrl || base.smallUrl, downloadUrl: base.originalUrl, downloadLabel: 'Open original' },
        res.previewUrl || res.thumbUrl ? { key: 'result', label: 'Result', src: res.previewUrl || res.thumbUrl, downloadUrl: res.originalUrl, downloadLabel: 'Download 4K original' } : null,
        item.detail && (item.detail.largeUrl || item.detail.smallUrl) ? {
          key: 'detail', label: 'Detail photo', src: item.detail.largeUrl || item.detail.originalUrl || item.detail.smallUrl,
          downloadUrl: item.detail.originalUrl, downloadLabel: 'Open original',
        } : null,
      ],
    });
  }, []);

  // ---- bulk regenerate ------------------------------------------------------------
  const openBulk = useCallback(() => {
    if (!jobId || (!checkAll && !checked.size)) return;
    setBulkSelection(checkAll
      ? {
        mode: 'filter',
        filter: {
          state: stateFilter, style: styleFilter, shape: shapeFilter, reason: reasonFilter || null, allAttempts: !!allAttempts,
          ai: aiFilter || undefined,
        },
      }
      : { mode: 'ids', genIds: [...checked].sort((a, b) => a - b) });
    // Review fix RP-4: picking the images through an AI problem filter means "regenerate by AI result": the AI's
    // notes start ticked, as from "Regenerate by AI result..." (the person can untick them).
    setBulkAiDefaults(['fail', 'look', 'not_pass'].includes(aiFilter) ? { useAiNotes: true } : null);
    // "Also send each rug's detail photo" starts ON for a Rug binding selection (build spec 4.3).
    const reasons = [...checked].map((g) => checkReasons.current.get(g));
    setBulkDetailDefault(checkAll
      ? reasonFilter === 'rug_binding'
      : (reasonFilter === 'rug_binding' || (reasons.length > 0 && reasons.every((r) => r === 'rug_binding'))));
    setBulkFromEarlier(!checkAll && [...checked].some((g) => checkEarlier.current.has(g)));
    setBulkOpen(true);
  }, [jobId, checkAll, checked, stateFilter, styleFilter, shapeFilter, reasonFilter, allAttempts, aiFilter]);

  const closeBulk = useCallback(() => { setBulkOpen(false); setBulkSelection(null); setBulkAiDefaults(null); }, []);

  // ---- AI check -------------------------------------------------------------------------
  // The Review list's rows as a selection filter (SPEC_AICHECK 4.7 "filter" = exactly these rows).
  const viewFilter = useMemo(() => ({
    state: stateFilter,
    style: styleFilter,
    shape: shapeFilter,
    reason: reasonFilter || null,
    allAttempts: !!allAttempts,
    ai: aiFilter || undefined,
  }), [stateFilter, styleFilter, shapeFilter, reasonFilter, allAttempts, aiFilter]);

  const openAiCheck = useCallback(() => {
    if (!jobId) return;
    setAiCheckSel({
      selection: checkAll
        ? { mode: 'filter', filter: viewFilter }
        : (checked.size ? { mode: 'ids', genIds: [...checked].sort((a, b) => a - b) } : null),
      selectAll: checkAll,
      total: (data && data.total) || 0,
      filter: viewFilter,
    });
  }, [jobId, checkAll, checked, viewFilter, data]);
  const onAiCheckDone = useCallback(() => { setAiCheckSel(null); load(); }, [load]);

  // Review fix RP-1: stop this job's checks still waiting, from the AI row, whatever the worker's check switch says
  // (the backend's cancel is not gated; running checks finish).
  const stopQueuedChecks = async () => {
    if (!jobId) return;
    setStopBusy(true);
    try {
      const res = await rrsApi.aiCheckCancel(jobId);
      const n = res && res.cancelled !== undefined ? res.cancelled : 0;
      notify(`Stopped ${fmtInt(n)} AI check${Number(n) === 1 ? '' : 's'}.`, 'info');
      setStopAsk(false);
      load();
    } catch (e) {
      notify(serverText(e), conflictCode(e) ? 'warning' : 'error');
    } finally {
      setStopBusy(false);
    }
  };

  const openApprove = useCallback(() => {
    if (!jobId) return;
    setApproveSel({
      filter: { style: styleFilter, shape: shapeFilter },
      ticked: !checkAll && checked.size ? [...checked].sort((a, b) => a - b) : null,
    });
  }, [jobId, checkAll, checked, styleFilter, shapeFilter]);
  const onApproveDone = useCallback(() => {
    setApproveSel(null);
    setChecked(new Set());
    setCheckAll(false);
    checkReasons.current = new Map();
    checkEarlier.current = new Set();
    load();
  }, [load]);

  // "Regenerate by AI result" -> the EXISTING bulk regenerate dialog with an AI filter (state All, the view's style
  // and shape, current images only), the AI's notes ticked and a default common note (SPEC_AICHECK 5.4.1).
  const onAiRegenContinue = useCallback((aiPart) => {
    setAiRegenOpen(false);
    setBulkSelection({
      mode: 'filter',
      filter: {
        state: 'all', style: styleFilter, shape: shapeFilter, reason: null, allAttempts: false, ...aiPart,
      },
    });
    setBulkDetailDefault(false);
    setBulkFromEarlier(false);
    setBulkAiDefaults({ useAiNotes: true, note: 'Fix the problems the AI check found' });
    setBulkOpen(true);
  }, [styleFilter, shapeFilter]);

  // Detail dialog "Use as regenerate note": the Regenerate dialog of that row, when it is on this page and can be
  // regenerated.
  const applyNoteFromDetail = (gen, note) => {
    const it = items.find((x) => x.genId === (gen && gen.genId));
    if (!it || !REGEN_FROM.includes(it.status) || it.supersededByGenId || isEarlierAttempt(it)) return;
    setDetailGen(null);
    openRegen(it);
    setRegenNote(String(note || '').slice(0, 1000));
    setRegenInPrompt(true);
  };
  const detailItem = detailGen ? items.find((x) => x.genId === detailGen) : null;
  const detailCanUseNote = !!detailItem && REGEN_FROM.includes(detailItem.status) && !detailItem.supersededByGenId
    && !isEarlierAttempt(detailItem);

  // Rows being committed show their spinner (and their buttons lock) until the request ends.
  const onBulkSubmitting = useCallback((genIds, on) => {
    setBusyGens((cur) => {
      const next = new Set(cur);
      (genIds || []).forEach((g) => { if (on) next.add(g); else next.delete(g); });
      return next;
    });
  }, []);

  const onBulkDone = useCallback((res) => {
    setChecked(new Set());
    setCheckAll(false);
    checkReasons.current = new Map();
    checkEarlier.current = new Set();
    setBulkOpen(false);
    setBulkSelection(null);
    load();
    if (res && isBatch(res.speed)) loadJobDetail();
  }, [load, loadJobDetail]);

  const pageCheckable = [...new Set(items.map(tickId).filter((g) => g !== null))];
  const reasonOfTick = (g) => {
    const row = items.find((it) => tickId(it) === g);
    return row ? row.reviewReasonCode : null;
  };
  // A page tick stands for an earlier attempt when any row of the page that ticks this genId is an earlier attempt.
  const earlierTick = (g) => items.some((it) => tickId(it) === g && isEarlierAttempt(it));
  const pageCheckedCount = pageCheckable.filter((g) => checked.has(g)).length;
  const pageAll = pageCheckable.length > 0 && pageCheckedCount === pageCheckable.length;
  const togglePage = () => {
    if (checkAll) return;
    setChecked((cur) => {
      const next = new Set(cur);
      if (pageAll) {
        pageCheckable.forEach((g) => { next.delete(g); checkReasons.current.delete(g); checkEarlier.current.delete(g); });
      } else {
        pageCheckable.forEach((g) => {
          next.add(g);
          checkReasons.current.set(g, reasonOfTick(g));
          if (earlierTick(g)) checkEarlier.current.add(g);
        });
      }
      return next;
    });
  };
  const clearChecks = () => {
    setChecked(new Set());
    setCheckAll(false);
    checkReasons.current = new Map();
    checkEarlier.current = new Set();
  };
  const total = (data && data.total) || 0;

  // ---- keyboard ----------------------------------------------------------------
  const dialogOpen = !!(rejectItem || regenItem || lightbox || detailGen || compareGenId || bulkOpen || downloadOpen
    || aiCheckSel || approveSel || aiSettingsOpen || aiRegenOpen || stopAsk);
  const onKeyDown = useCallback((e) => {
    if (dialogOpen || e.ctrlKey || e.metaKey || e.altKey || isTyping(e.target)) return;
    const item = items[selected];
    const k = e.key.toLowerCase();
    if (k === 'j') { moveSelection(1); e.preventDefault(); }
    else if (k === 'k') { moveSelection(-1); e.preventDefault(); }
    else if (k === 'a' && item) { approve(item); e.preventDefault(); }
    else if (k === 'r' && item) { openReject(item); e.preventDefault(); }
    else if (k === 'g' && item) { openRegen(item); e.preventDefault(); }
    else if (k === 'c' && item) { openCompare(item); e.preventDefault(); }
    else if (k === 'x' && item && checkable(item) && !checkAll) {
      toggleCheck(tickId(item), item.reviewReasonCode, isEarlierAttempt(item));
      e.preventDefault();
    }
  }, [dialogOpen, items, selected, moveSelection, approve, openReject, openRegen, openCompare, checkAll, toggleCheck]);

  useEffect(() => {
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onKeyDown]);

  // ---- filters ------------------------------------------------------------------
  // Picking a reason sets Rejected and turns "Include earlier attempts" on (every rejected image so far was
  // regenerated, so only earlier attempts are rejected). The user may turn the switch off again.
  // AI filters show current images only (SPEC_AICHECK 4.5): a Reason or "Include earlier attempts" clears the AI
  // filter, and an AI filter turns earlier attempts off and clears the Reason.
  const onReasonChange = (v) => {
    setPage(0);
    resetSelection();
    setReasonFilter(v);
    if (v) { setStateFilter('rejected'); setAllAttempts(true); setAiFilter(''); }
  };
  const onStateChip = (key) => {
    setPage(0);
    resetSelection();
    setStateFilter(key);
    if (key === 'rejected') { setAllAttempts(true); setAiFilter(''); } else { setAllAttempts(false); setReasonFilter(''); }
  };
  const onAiFilterChange = (v) => {
    setPage(0);
    resetSelection();
    setAiFilter(v);
    if (v) { setAllAttempts(false); setReasonFilter(''); }
  };
  const onSortChange = (v) => {
    setPage(0);
    resetSelection();
    setSortOrder(v);
  };
  const aiFilterCount = (v) => {
    if (!aiCounts) return 0;
    if (v === 'not_pass') return (Number(aiCounts.fail) || 0) + (Number(aiCounts.look) || 0);
    return Number(aiCounts[v]) || 0;
  };
  const aiLine = aiAvailable ? aiCountsText(aiCounts) : '';
  const reasonCounts = (data && data.reasonCounts) || null;

  // ---- render -------------------------------------------------------------------
  const currentJob = jobs.find((j) => j.jobId === jobId);
  const pct = progress.total ? Math.round((progress.done / progress.total) * 100) : 0;

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
        <TextField
          select size="small" label="Job" value={jobId || ''} sx={{ minWidth: 320 }}
          onChange={(e) => onJobChange(Number(e.target.value))}
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } }}
        >
          {jobId && !currentJob && <MenuItem value={jobId}>Job #{jobId}</MenuItem>}
          {jobs.map((j) => (
            <MenuItem key={j.jobId} value={j.jobId}>
              #{j.jobId} · {j.name} · {(JOB_STATUS[j.status] || {}).label || j.status}
            </MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Style" value={styleFilter} sx={{ minWidth: 160 }} onChange={(e) => { setPage(0); setStyleFilter(e.target.value); }}>
          <MenuItem value="">All</MenuItem>
          {styles.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Shape" value={shapeFilter} sx={{ minWidth: 180 }} onChange={(e) => { setPage(0); setShapeFilter(e.target.value); }}>
          <MenuItem value="">All</MenuItem>
          {config.shapes.map((s) => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
        </TextField>
        <TextField
          select size="small" label="Reason" value={reasonFilter} sx={{ minWidth: 200 }}
          onChange={(e) => onReasonChange(e.target.value)}
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } }}
        >
          <MenuItem value="">All reasons</MenuItem>
          {(config.rejectReasons || []).map((r) => (
            <MenuItem key={r} value={r}>
              {REJECT_REASON_LABELS[r] || r}{reasonCounts ? ` (${fmtInt(reasonCounts[r] || 0)})` : ''}
            </MenuItem>
          ))}
        </TextField>
        <Box sx={{ flex: 1 }} />
        {loading && <CircularProgress size={20} />}
        <Tooltip title="Keyboard shortcuts"><Button size="small" startIcon={<MdKeyboard />} onClick={() => setShowKeys((s) => !s)}>Keys</Button></Tooltip>
        <Button size="small" startIcon={<MdRefresh />} onClick={() => { loadJobs(); load(); }} disabled={!jobId}>Refresh</Button>
        <Tooltip title="Save this job's images into a folder, or build zip files">
          <span>
            <Button size="small" variant="outlined" startIcon={<MdDownload />} onClick={() => setDownloadOpen(true)} disabled={!jobId}>
              Download images…
            </Button>
          </span>
        </Tooltip>
      </Stack>

      {/* AI check row (SPEC_AICHECK 5.4): filter, order and the AI actions. Hidden with an older backend. */}
      {jobId && aiAvailable && (
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 1.5 }}>
          <TextField
            select size="small" label="AI check" value={aiFilter} sx={{ minWidth: 240 }}
            onChange={(e) => onAiFilterChange(e.target.value)}
            SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } }}
          >
            {AI_FILTERS.map(([v, label]) => (
              <MenuItem key={v || 'all'} value={v}>
                {label}{v ? ` (${fmtInt(aiFilterCount(v))})` : ''}
              </MenuItem>
            ))}
          </TextField>
          <TextField select size="small" label="Order" value={sortOrder} sx={{ minWidth: 190 }} onChange={(e) => onSortChange(e.target.value)}>
            <MenuItem value="default">Plan order</MenuItem>
            <MenuItem value="ai_worst">AI: worst first</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Tooltip title={aiOn ? 'Check images with AI: you see the count and the cost first' : AI_NOT_READY_TEXT}>
              <span>
                <Button size="small" variant="outlined" startIcon={<MdFactCheck />} disabled={!aiOn} onClick={openAiCheck}>
                  Check with AI…
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Regenerate the images the AI check failed (or scored below a number you pick), each with the AI's note">
              <Button
                size="small" variant="outlined" startIcon={<MdReplay />} onClick={() => setAiRegenOpen(true)}
                sx={{ color: aiAccent, borderColor: `${aiAccent}88` }}
              >
                Regenerate by AI result…
              </Button>
            </Tooltip>
            <Tooltip title={aiFilterCount('pass') ? 'Approve, in your name, the images the AI check passed at a score you pick' : 'No image has an AI Pass in this view.'}>
              <span>
                <Button size="small" variant="outlined" color="success" startIcon={<MdDoneAll />} disabled={!aiFilterCount('pass')} onClick={openApprove}>
                  Approve AI-passed…
                </Button>
              </span>
            </Tooltip>
            {aiQueued > 0 && (
              <Tooltip title="Stop this job's AI checks that are still waiting (checks already running finish)">
                <Button
                  size="small" variant="outlined" color="warning" startIcon={<MdStopCircle />}
                  onClick={() => setStopAsk(true)} disabled={stopBusy}
                >
                  Stop queued checks ({fmtInt(aiQueued)})
                </Button>
              </Tooltip>
            )}
            <Tooltip title="AI check settings">
              <IconButton size="small" onClick={() => setAiSettingsOpen(true)} aria-label="AI check settings"><MdSettings /></IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      )}

      <Collapse in={showKeys}>
        <Alert severity="info" sx={{ mb: 1.5 }}>
          <strong>A</strong> approve · <strong>R</strong> reject · <strong>G</strong> regenerate · <strong>C</strong> compare side by side · <strong>J</strong> / <strong>K</strong> next / previous row
          {' '}· <strong>X</strong> tick / untick the row for Regenerate selected.
          Click a row to select it. Shortcuts are off while a dialog is open or you are typing.
        </Alert>
      </Collapse>

      {!jobId && <Alert severity="info">Pick a job to review. Jobs appear here after Start.</Alert>}
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {jobId && data && (
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.75, flexWrap: 'wrap' }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {fmtInt(progress.done)} of {fmtInt(progress.total)} done
              {atGoogle ? ` · ${fmtInt(atGoogle)} at Google (half price)` : ''}
              {progress.queued || !atGoogle ? ` · ${fmtInt(progress.queued)} queued` : ''} · {fmtInt(progress.running)} generating
              {progress.needsReview ? ` · ${fmtInt(progress.needsReview)} need review` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {fastActive ? 'Updating every 5 seconds' : (aiMoving ? 'AI checks running: updating every 10 seconds' : (batchActive ? 'Half price: checking every minute; Google usually returns within hours, up to 24 h' : 'Nothing in progress'))}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate" value={pct}
            sx={{ height: 8, borderRadius: 4, bgcolor: theme.palette.action.hover, '& .MuiLinearProgress-bar': { bgcolor: AI_COLOR } }}
          />
          {aiAvailable && (aiLine || aiWaiting > 0) && (
            <Tooltip title="The AI results of the images in this view (state, style, shape and reason filters). Checks waiting or running: the whole job.">
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>AI check:</Box>
                {' '}{[aiLine, aiWaiting > 0 ? `${fmtInt(aiWaiting)} AI check${aiWaiting === 1 ? '' : 's'} waiting or running${aiOn ? '' : ' (the AI check is not running: they wait until it is back, or Stop queued checks)'}` : ''].filter(Boolean).join(' · ')}
              </Typography>
            </Tooltip>
          )}
        </Paper>
      )}

      {jobId && jobDetail && <BatchesSection batches={jobDetail.batches} now={now} />}

      {jobId && (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', rowGap: 1 }}>
          {STATE_FILTERS.map((f) => (
            <Chip
              key={f.key} label={f.label} clickable
              color={stateFilter === f.key ? 'primary' : 'default'}
              variant={stateFilter === f.key ? 'filled' : 'outlined'}
              onClick={() => onStateChip(f.key)}
              sx={stateFilter === f.key ? { bgcolor: AI_COLOR, color: '#fff' } : undefined}
            />
          ))}
          <Tooltip title="Also list the earlier attempts of each rug (view only). Rejected images that were regenerated are earlier attempts.">
            <FormControlLabel
              sx={{ ml: 1 }}
              control={(
                <Switch
                  size="small" checked={allAttempts}
                  onChange={(e) => {
                    setPage(0);
                    resetSelection();
                    setAllAttempts(e.target.checked);
                    if (e.target.checked) setAiFilter('');
                  }}
                />
              )}
              label={<Typography variant="body2">Include earlier attempts</Typography>}
            />
          </Tooltip>
        </Stack>
      )}

      {jobId && data && items.length === 0 && !loading && (
        <Paper variant="outlined" sx={{ p: 4, borderRadius: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {aiFilter ? 'No image matches this AI filter.' : stateFilter === 'pending' ? 'Nothing waiting for review here.' : (stateFilter === 'rejected' && !allAttempts
              ? 'No current image is rejected. Rejected images that were regenerated are earlier attempts: switch on Include earlier attempts.'
              : 'Nothing matches these filters.')}
          </Typography>
        </Paper>
      )}

      {jobId && items.length > 0 && (
        <Paper variant="outlined" sx={{ px: 1.5, py: 1, borderRadius: 2, mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <FormControlLabel
              sx={{ mr: 0 }}
              control={(
                <Checkbox
                  size="small"
                  checked={checkAll ? pageCheckable.length > 0 : pageAll}
                  indeterminate={!checkAll && pageCheckedCount > 0 && !pageAll}
                  disabled={checkAll || pageCheckable.length === 0}
                  onChange={togglePage}
                />
              )}
              label={<Typography variant="body2">Select page ({fmtInt(pageCheckable.length)} can be regenerated)</Typography>}
            />
            {!checkAll && pageAll && total > items.length && (
              <Button size="small" onClick={() => setCheckAll(true)}>
                {allAttempts
                  ? `Select the current image of every rug in these ${fmtInt(total)} rows`
                  : `Select all ${fmtInt(total)} matching this filter`}
              </Button>
            )}
            {checkAll ? (
              <Typography variant="body2" color="text.secondary">
                {allAttempts
                  ? `The current image of every rug in these ${fmtInt(total)} rows is selected (images still in progress are skipped).`
                  : `All ${fmtInt(total)} matching this filter are selected (rows still in progress or with a newer attempt are skipped).`}
              </Typography>
            ) : (checked.size > 0 && (
              <Typography variant="body2" color="text.secondary">
                {allAttempts ? `${fmtInt(checked.size)} current image${checked.size === 1 ? '' : 's'} selected` : `${fmtInt(checked.size)} selected`}
              </Typography>
            ))}
            {(checkAll || checked.size > 0) && <Button size="small" onClick={clearChecks}>Clear</Button>}
            <Box sx={{ flex: 1 }} />
            <Button
              size="small" variant="contained" startIcon={<MdReplay />} sx={primaryButtonSx}
              disabled={!(checkAll || checked.size > 0)} onClick={openBulk}
            >
              Regenerate selected…
            </Button>
          </Stack>
        </Paper>
      )}

      <Stack spacing={1.5}>
        {items.map((it, idx) => (
          <ReviewRow
            key={it.genId}
            ref={(el) => { rowRefs.current[it.genId] = el; }}
            item={it}
            selected={idx === selected}
            onSelect={() => setSelected(idx)}
            now={now}
            styles={styles}
            shapes={config.shapes}
            usdPerImage={rateFor(defaultSpeed(jobSpeed || it.speed, speeds), speeds, jobDetail)}
            busy={busyGens.has(it.genId)}
            onApprove={() => { setSelected(idx); approve(it); }}
            onReject={() => { setSelected(idx); openReject(it); }}
            onRegenerate={() => { setSelected(idx); openRegen(it); }}
            onClear={() => decide(it, { decision: 'clear' })}
            onOpenLightbox={(key) => openLightbox(it, key)}
            onDetails={(genId) => setDetailGen(genId)}
            onCompare={() => { setSelected(idx); openCompare(it); }}
            checked={checked.has(tickId(it))}
            checkable={checkable(it)}
            checkLocked={checkAll}
            checkTotal={total}
            onCheck={() => toggleCheck(tickId(it), it.reviewReasonCode, isEarlierAttempt(it))}
            letterNames={config.roomLetters}
          />
        ))}
      </Stack>

      {jobId && data && (data.total || 0) > PAGE_SIZE && (
        <TablePagination
          component="div" count={data.total || 0} page={page}
          onPageChange={(e, p) => { setPage(p); resetSelection(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          rowsPerPage={PAGE_SIZE} rowsPerPageOptions={[PAGE_SIZE]}
        />
      )}

      {/* Reject dialog */}
      <Dialog open={!!rejectItem} onClose={rejectBusy ? undefined : () => setRejectItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject {rejectItem ? rejectItem.itemId : ''}</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth size="small" label="Reason" value={rejectReason} sx={{ mt: 1 }}
            onChange={(e) => setRejectReason(e.target.value)} autoFocus
            helperText={rejectSuggested && rejectReason === rejectSuggested ? 'Suggested by the AI check.' : undefined}
          >
            {(config.rejectReasons || []).map((r) => {
              const blocked = reasonBlocked(rejectItem, r);
              return (
                <MenuItem key={r} value={r} disabled={blocked}>
                  <ListItemText
                    primary={REJECT_REASON_LABELS[r] || r}
                    secondary={blocked ? WRONG_STYLE_TWIN_TIP : null}
                    sx={{ my: 0 }}
                  />
                </MenuItem>
              );
            })}
          </TextField>
          {rejectReason === 'wrong_style' && (
            <TextField
              select fullWidth size="small" label="Correct style" value={rejectStyle} sx={{ mt: 2 }}
              onChange={(e) => setRejectStyle(e.target.value)}
              helperText="Teaches the Style Sorter and changes the rug's style; regenerate afterwards to use a room of that style. Sunroom comes only from the Outdoor & sunroom list."
            >
              {manualStyles(styles).filter((s) => !rejectItem || s.code !== rejectItem.styleCode).map((s) => <MenuItem key={s.code} value={s.name}>{s.name}</MenuItem>)}
            </TextField>
          )}
          <TextField
            fullWidth size="small" label="Note (optional)" value={rejectNote} sx={{ mt: 2 }} multiline minRows={2}
            onChange={(e) => setRejectNote(e.target.value.slice(0, 1000))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectItem(null)} disabled={rejectBusy}>Cancel</Button>
          <Button
            variant="contained" color="error" onClick={submitReject}
            disabled={rejectBusy || !rejectReason || reasonBlocked(rejectItem, rejectReason) || (rejectReason === 'wrong_style' && !rejectStyle)}
          >
            {rejectBusy ? <CircularProgress size={20} color="inherit" /> : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Regenerate dialog */}
      <Dialog open={!!regenItem} onClose={regenBusy ? undefined : () => setRegenItem(null)} maxWidth="md" fullWidth>
        {regenItem && (
          <>
            <DialogTitle>
              {regenWord(regenItem.status)} {regenItem.itemId}
              <Typography variant="body2" color="text.secondary">
                Attempt {regenItem.attempt + 1} · {shapeName(regenItem.shapeCode, config.shapes)} · currently {regenItem.roomCode}
                {regenItem.status === 'unknown' ? ' · the previous attempt may already have been billed' : ''}
              </Typography>
            </DialogTitle>
            <DialogContent dividers>
              <TextField
                fullWidth size="small" label="What should change? (required, logged)" value={regenNote} autoFocus multiline minRows={2}
                onChange={(e) => setRegenNote(e.target.value.slice(0, 1000))}
                error={regenNote.length > 0 && regenNote.trim().length < 3}
                helperText={regenNote.trim().length < 3 ? 'At least 3 characters.' : ' '}
              />
              {regenItem.aiCheck && regenItem.aiCheck.suggestedNote && (
                <Tooltip title={regenItem.aiCheck.suggestedNote}>
                  <Button
                    size="small" startIcon={<MdAutoFixHigh />} sx={{ mb: 0.5 }}
                    onClick={() => { setRegenNote(String(regenItem.aiCheck.suggestedNote).slice(0, 1000)); setRegenInPrompt(true); }}
                  >
                    Use the AI&apos;s note
                  </Button>
                </Tooltip>
              )}
              <FormControlLabel
                control={<Checkbox checked={regenInPrompt} onChange={(e) => setRegenInPrompt(e.target.checked)} />}
                label="Add my note to the prompt"
              />
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Off: the same prompt is sent again (the note is only logged). On: your note is appended to the standard prompt.
              </Typography>
              <RadioGroup row value={regenRoomMode} onChange={(e) => setRegenRoomMode(e.target.value)}>
                <FormControlLabel value="same" control={<Radio />} label={`Same room (${regenItem.roomCode})`} />
                <FormControlLabel value="other" control={<Radio />} label="Choose the base room" />
              </RadioGroup>
              {regenItem.targetStyleCode && regenItem.targetStyleCode !== regenItem.styleCode && (
                <Alert severity="info" sx={{ my: 1 }}>
                  The rug&apos;s style changed to {styleName(regenItem.targetStyleCode, styles)} since this attempt; the least-used {styleName(regenItem.targetStyleCode, styles)} room is preselected.
                </Alert>
              )}
              {regenRoomMode === 'other' && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Click a room to choose it; click its photo to see it large.
                  </Typography>
                  <RoomPicker
                    shapeCode={regenItem.shapeCode}
                    styleCode={regenItem.targetStyleCode || regenItem.styleCode}
                    value={regenRoom}
                    onChange={(code, room) => { setRegenRoom(code); setRegenRoomObj(room || null); }}
                    onLoaded={onRegenRoomsLoadedAll}
                    onPreview={(r) => setRoomPreview(r)}
                    styles={styles}
                    currentRoomCode={regenItem.roomCode}
                    allowedLetters={regenItem.roomLetters}
                    scene={regenItem.scene}
                    letterNames={config.roomLetters}
                  />
                  {regenWarnings.map((w) => <Alert key={w} severity="warning" sx={{ mt: 1 }}>{w} You can still use it.</Alert>)}
                </Box>
              )}
              <DetailPhotoBlock
                targetId={regenItem.targetId}
                reviewReasonCode={regenItem.reviewReasonCode}
                config={config}
                value={regenDetail}
                onChange={setRegenDetail}
                disabled={regenBusy}
                notify={notify}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 2 }}>Speed</Typography>
              <SpeedChooser value={regenSpeed} onChange={setRegenSpeed} speeds={speeds} job={jobDetail} />
              {overBudget(config, rateFor(regenSpeed, speeds, jobDetail)) && (
                <Alert severity="warning" sx={{ mt: 1.5 }}>{overBudgetText(config, rateFor(regenSpeed, speeds, jobDetail))}</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRegenItem(null)} disabled={regenBusy}>Cancel</Button>
              <Button
                variant="contained" onClick={submitRegen} sx={primaryButtonSx}
                disabled={regenBusy || regenNote.trim().length < 3 || (regenRoomMode === 'other' && !regenRoom) || !detailReady(regenDetail)}
              >
                {regenBusy ? <CircularProgress size={20} color="inherit" /> : `${regenWord(regenItem.status)} · ${fmtRate(rateFor(regenSpeed, speeds, jobDetail))}`}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ConfirmDialog
        open={stopAsk} busy={stopBusy} onClose={() => setStopAsk(false)} onConfirm={stopQueuedChecks}
        title="Stop queued checks?" confirmLabel="Stop" confirmColor="warning"
      >
        <Typography>
          Stop the {fmtInt(aiQueued)} AI check{aiQueued === 1 ? '' : 's'} still waiting? Checks already running finish.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Only this job&apos;s checks stop. Check with AI can check those images again later.
        </Typography>
      </ConfirmDialog>

      <ImageLightbox
        open={!!lightbox} onClose={() => setLightbox(null)}
        items={lightbox ? lightbox.items : []} initialKey={lightbox ? lightbox.initialKey : null}
        title={lightbox ? lightbox.title : ''}
      />
      {/* A base room from the room picker, large (the picker stays open underneath). */}
      <ImageLightbox
        open={!!roomPreview} onClose={() => setRoomPreview(null)}
        items={roomPreview && roomPreview.base ? [{
          key: 'base', label: 'Base room', src: roomPreview.base.largeUrl || roomPreview.base.originalUrl || roomPreview.base.smallUrl,
          downloadUrl: roomPreview.base.originalUrl, downloadLabel: 'Open original',
        }] : []}
        initialKey="base" title={roomPreview ? `${roomPreview.roomCode}${roomPreview.base && roomPreview.base.imageKey ? ` · ${roomPreview.base.imageKey}` : ''}` : ''}
      />
      <GenerationDetailDialog
        genId={detailGen} onClose={() => setDetailGen(null)} aiEnabled={aiOn} onAiChanged={load}
        onUseNote={detailCanUseNote ? applyNoteFromDetail : undefined}
      />

      {jobId && downloadOpen && (
        <DownloadDialog open onClose={() => setDownloadOpen(false)} jobId={jobId} config={config} notify={notify} />
      )}

      {/* Mounted only while open: every open gets fresh state and a fresh clientRef. */}
      {jobId && bulkOpen && bulkSelection && (
        <BulkRegenerateDialog
          open
          onClose={closeBulk}
          jobId={jobId}
          selection={bulkSelection}
          jobDetail={jobDetail}
          speeds={speeds}
          styles={styles}
          config={config}
          notify={notify}
          onSubmitting={onBulkSubmitting}
          onDone={onBulkDone}
          onRefresh={load}
          defaultDetail={bulkDetailDefault}
          fromEarlierAttempts={bulkFromEarlier}
          defaultUseAiNotes={!!(bulkAiDefaults && bulkAiDefaults.useAiNotes)}
          defaultNote={bulkAiDefaults ? bulkAiDefaults.note : undefined}
        />
      )}

      {/* AI check dialogs (SPEC_AICHECK 5.4-5.8), each mounted only while open. */}
      {jobId && aiCheckSel && (
        <AiCheckDialog
          open
          onClose={() => setAiCheckSel(null)}
          jobId={jobId}
          selection={aiCheckSel.selection}
          selectAll={aiCheckSel.selectAll}
          total={aiCheckSel.total}
          filter={aiCheckSel.filter}
          config={config}
          notify={notify}
          onDone={onAiCheckDone}
          onRefresh={load}
          queuedCount={aiQueued}
        />
      )}
      {jobId && approveSel && (
        <BulkApproveDialog
          open
          onClose={() => setApproveSel(null)}
          jobId={jobId}
          filter={approveSel.filter}
          ticked={approveSel.ticked}
          config={config}
          notify={notify}
          onDone={onApproveDone}
          onRefresh={load}
          aiThresholds={aiThresholds}
        />
      )}
      {jobId && aiRegenOpen && (
        <AiRegenerateChooser
          open
          onClose={() => setAiRegenOpen(false)}
          jobId={jobId}
          style={styleFilter}
          shape={shapeFilter}
          config={config}
          aiThresholds={aiThresholds}
          onContinue={onAiRegenContinue}
        />
      )}
      {aiSettingsOpen && (
        <AiCheckSettingsDialog open onClose={() => setAiSettingsOpen(false)} notify={notify} onSaved={() => load()} />
      )}

      {jobId && compareGenId && (
        <CompareDialog
          genId={compareGenId}
          items={items}
          itemsPage={data && data.page ? data.page - 1 : page}
          page={page}
          pageSize={PAGE_SIZE}
          total={(data && data.total) || 0}
          ready={!!data}
          loading={loading}
          stateFilter={stateFilter}
          config={config}
          speeds={speeds}
          jobSpeed={jobSpeed}
          job={jobDetail}
          busyGenIds={busyGens}
          onNavigate={onCompareNavigate}
          onPageChange={onComparePage}
          onClose={closeCompare}
          onDecide={decide}
          onRegenerate={doRegenerate}
          notify={notify}
          aiEnabled={aiOn}
          onAiCheck={requestCheck}
          onRefresh={load}
        />
      )}
    </Box>
  );
};

export default ReviewTab;
